import { motion } from "motion/react";

export default function AnalyticsView() {
  const kpis = [
    {
      label: "Total Batches",
      value: "2,847",
      change: "+12.3%",
      changePositive: true,
      icon: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M2 8h3l2-4 3 8 2-4h3" stroke="#8B93A5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Pass Rate",
      value: "98.3%",
      change: "+2.1%",
      changePositive: true,
      icon: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M13 4l-6 6-3-3" stroke="#8B93A5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Avg Strength",
      value: "32.8",
      unit: "MPa",
      change: "+4.2%",
      changePositive: true,
      icon: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M9 2L3 8h5l-1 6 6-6h-5l1-6z" stroke="#8B93A5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  const monthlyData = [
    { month: "Jan", batches: 220 },
    { month: "Feb", batches: 245 },
    { month: "Mar", batches: 268 },
    { month: "Apr", batches: 251 },
    { month: "May", batches: 289 },
    { month: "Jun", batches: 301 },
  ];

  const gradeDistribution = [
    { grade: "M30", count: 1247, percentage: 43.8 },
    { grade: "M25", count: 856, percentage: 30.1 },
    { grade: "M20", count: 534, percentage: 18.8 },
    { grade: "M35", count: 210, percentage: 7.3 },
  ];

  const maxBatches = Math.max(...monthlyData.map((d) => d.batches));

  return (
    <div className="pb-24" style={{ background: "#F9FAFB", minHeight: "100vh" }}>
      <div className="px-5 pt-6 pb-4">
        <h1
          style={{
            fontSize: "28px",
            letterSpacing: "-0.02em",
            fontWeight: "700",
            color: "#0A0F1C",
          }}
        >
          Analytics v2.0
        </h1>
        <p
          style={{ fontSize: "14px", fontWeight: "500", color: "#5B6475" }}
        >
          Performance insights & trends
        </p>
      </div>

      {/* KPI Cards - Ultra Compact */}
      <div className="px-4 mb-5">
        <div className="flex gap-2">
          {kpis.map((kpi, index) => (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              className="flex-1 rounded-lg bg-white"
              style={{
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="p-2">
                {/* Icon - Ultra Small */}
                <div className="mb-1.5">
                  {kpi.icon}
                </div>

                {/* Value - Compact */}
                <div className="mb-0.5">
                  {kpi.unit ? (
                    <div className="flex items-baseline gap-0.5">
                      <div
                        style={{
                          fontSize: "15px",
                          letterSpacing: "-0.02em",
                          fontWeight: "700",
                          color: "#0A0F1C",
                          lineHeight: "1",
                        }}
                      >
                        {kpi.value}
                      </div>
                      <div
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          color: "#8B93A5",
                        }}
                      >
                        {kpi.unit}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "15px",
                        letterSpacing: "-0.02em",
                        fontWeight: "700",
                        color: "#0A0F1C",
                        lineHeight: "1",
                      }}
                    >
                      {kpi.value}
                    </div>
                  )}
                </div>

                {/* Label - Ultra Small */}
                <div
                  style={{
                    fontSize: "8px",
                    fontWeight: "600",
                    color: "#8B93A5",
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                    marginBottom: "4px",
                    lineHeight: "1.1",
                  }}
                >
                  {kpi.label}
                </div>

                {/* Change - Subtle */}
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: "600",
                    color: kpi.changePositive ? "#5B6475" : "#EF4444",
                  }}
                >
                  {kpi.change}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Monthly Trend - Clean Bar Chart */}
      <div className="px-4 mb-5">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          className="bg-white rounded-2xl p-4"
          style={{
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#0A0F1C",
                }}
              >
                Monthly Production
              </div>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#8B93A5",
                  marginTop: "2px",
                }}
              >
                Last 6 months
              </div>
            </div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                color: "#10B981",
              }}
            >
              +18.2%
            </div>
          </div>

          <div className="flex items-end justify-between gap-2 h-32">
            {monthlyData.map((data, index) => (
              <div
                key={data.month}
                className="flex-1 flex flex-col items-center gap-2"
              >
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(data.batches / maxBatches) * 100}%` }}
                  transition={{ delay: 0.3 + index * 0.05, duration: 0.4 }}
                  className="w-full rounded-t-md"
                  style={{
                    backgroundColor: "#1B2A5B",
                    minHeight: "20px",
                  }}
                />
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: "600",
                    color: "#8B93A5",
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                  }}
                >
                  {data.month}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Grade Distribution - Minimal Progress Bars */}
      <div className="px-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.3 }}
          className="bg-white rounded-2xl p-4"
          style={{
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
          }}
        >
          <div
            className="mb-4"
            style={{
              fontSize: "15px",
              fontWeight: "700",
              color: "#0A0F1C",
            }}
          >
            Grade Distribution
          </div>

          <div className="space-y-3">
            {gradeDistribution.map((item, index) => (
              <motion.div
                key={item.grade}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.05 }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#0A0F1C",
                    }}
                  >
                    {item.grade}
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#8B93A5",
                      }}
                    >
                      {item.count.toLocaleString()}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#5B6475",
                        minWidth: "42px",
                        textAlign: "right",
                      }}
                    >
                      {item.percentage}%
                    </div>
                  </div>
                </div>
                <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.percentage}%` }}
                    transition={{ delay: 0.5 + index * 0.05, duration: 0.5 }}
                    className="h-full rounded-full"
                    style={{
                      backgroundColor: "#1B2A5B",
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}